import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mapApiError } from '@/lib/api/error.js';
import { Card } from '@/components/ui/card.js';
import { Icon } from '@/components/ui/icon.js';
import { Money } from '@/components/money/Money.js';
import { useFormatMoney, useMoneyCurrency } from '@/components/money/MoneyProvider.js';
import { BonusSectionHeader } from '@/components/promo/BonusSectionHeader.js';
import {
  REFERRAL_QUERY_KEYS,
  fetchReferralSummary,
  fetchReferralFriends,
  claimReferral,
  buildReferralLink,
  shareOrigin,
} from '@/features/referral/api.js';
import { fetchMemberMe, MEMBER_QUERY_KEYS } from '@/features/member/api.js';
import { bangkokDayKey, formatBangkokDayKey } from '@/lib/datetime/bangkok.js';
import { formatDateTime } from '@/lib/datetime/format.js';
import type { IconName } from '@/components/ui/icon.js';
import { useBrandingStore } from '@/stores/branding.js';

export function ReferralSection() {
  const { t, i18n } = useTranslation('referral');
  const currency = useMoneyCurrency();
  const formatMoney = useFormatMoney();
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);

  const summary = useQuery({
    queryKey: REFERRAL_QUERY_KEYS.summary,
    queryFn: fetchReferralSummary,
  });

  const friends = useQuery({
    queryKey: REFERRAL_QUERY_KEYS.friends,
    queryFn: fetchReferralFriends,
  });

  const me = useQuery({
    queryKey: MEMBER_QUERY_KEYS.me,
    queryFn: fetchMemberMe,
  });

  const claimable = BigInt(summary.data?.claimable ?? '0');
  // `nextOpenDay` counts today, so it equals today's key whenever the window is
  // open now. Both sides are BANGKOK day keys — comparing against the device's
  // own calendar would open the window seven hours early in Europe.
  const windowClosed =
    summary.data?.nextOpenDay != null && summary.data.nextOpenDay > bangkokDayKey(new Date());
  const outOfClaimsToday = summary.data?.claimsLeftToday != null && summary.data.claimsLeftToday <= 0;
  const expiringMinor = BigInt(summary.data?.expiringAmount ?? '0');
  // The floor the claim path refuses under. Absent from a service that predates
  // it, which reads as no floor — the button then behaves exactly as it did.
  const minClaim = BigInt(summary.data?.minClaimAmount ?? '0');
  const belowThreshold = minClaim > 0n && claimable > 0n && claimable < minClaim;

  const claim = useMutation({
    mutationFn: claimReferral,
    onSuccess: (result) => {
      // Only the call that actually posted to the wallet may say the money
      // arrived (#444). A same-minute retry reads back the row an earlier call
      // wrote and answers 200 with its amount, and a row whose wallet credit is
      // unconfirmed does not read 'completed' — announcing either as a success
      // is announcing a payout that may not exist.
      if (result.status !== 'completed') toast.warning(t('claimPending'));
      else if (result.replay) toast.info(t('claimAlreadyClaimed'));
      // The tap crossed a forfeit: part of the balance was written off and the
      // rest was paid, both inside this one tap (#518). Naming the forfeited
      // figure is the whole point — a plain "claimed" against a wallet that
      // grew by less than the screen showed reads as money going missing.
      else if (result.forfeitedFirst) {
        toast.warning(t('claimAfterForfeit', { forfeited: formatMoney(expiringMinor, currency) }));
      } else toast.success(t('claimSuccess'));
      void qc.invalidateQueries({ queryKey: REFERRAL_QUERY_KEYS.summary });
      void qc.invalidateQueries({ queryKey: REFERRAL_QUERY_KEYS.claims });
    },
    onError: (err) => toast.error(mapApiError(err, t)),
  });

  // Everything the player needs to know BEFORE tapping. Nothing renders on the
  // default realtime, no-expiry config every un-migrated agent still runs.
  const cadenceNotes: Array<{ key: string; icon: IconName; body: ReactNode }> = [];
  if (summary.data?.claimMode && summary.data.claimMode !== 'realtime') {
    cadenceNotes.push({
      key: 'mode',
      icon: 'history',
      body: t(`cadence.mode.${summary.data.claimMode}`),
    });
  }
  if (summary.data?.nextOpenDay) {
    cadenceNotes.push({
      key: 'nextOpenDay',
      icon: 'history',
      body: windowClosed
        ? t('cadence.opensOn', { day: formatBangkokDayKey(summary.data.nextOpenDay, i18n.language) })
        : t('cadence.openToday'),
    });
  }
  if (summary.data?.claimsLeftToday != null) {
    cadenceNotes.push({
      key: 'claimsLeft',
      icon: 'ticket',
      body: t('cadence.claimsLeftToday', { count: summary.data.claimsLeftToday }),
    });
  }
  // `expired` is the whole fact: the server sets it and `expiringAmount` from
  // the same `dueAmount`, so a non-null amount cannot occur without it. And
  // once it is true the money is unrecoverable — the next claim attempt BOOKS
  // the forfeit and then refuses with REFERRAL-009 — so the copy is past tense.
  // Warning a player about money that has already gone is worse than silence.
  const forfeited = summary.data?.expired === true;
  if (forfeited) {
    cadenceNotes.push({
      key: 'expiring',
      icon: 'bell',
      body: (
        <>
          {t('cadence.expiredAmount')} <Money minor={expiringMinor} />
          {claimable > 0n ? ` ${t('cadence.expiredRemainder')}` : null}
        </>
      ),
    });
  }
  if (summary.data?.forfeitAt) {
    const at = formatDateTime(summary.data.forfeitAt, i18n.language);
    // The un-expired branch is the only one a warning can still help in, and it
    // is the only one whose deadline is an estimate: it is dated from the window
    // without re-checking the balance at the opening, so it may name a forfeit
    // that never happens. A deadline that has already passed is not a guess.
    cadenceNotes.push({
      key: 'forfeitAt',
      icon: 'bell',
      body: forfeited ? t('cadence.forfeitedAt', { at }) : t('cadence.forfeitAt', { at }),
    });
  }
  // A dead button with no explanation is what sends players to support (#513).
  // Ordered by how absolute the block is: a programme that is off cannot be
  // worked around, a closed window can be waited out, a floor can be earned
  // past. An EMPTY balance needs no line — the button's own label says it.
  const disabledReason: ReactNode =
    summary.data?.enabled === false
      ? t('disabledReason.programOff')
      : windowClosed
        ? t('disabledReason.windowClosed')
        : outOfClaimsToday
          ? t('disabledReason.noClaimsLeft')
          : belowThreshold
            ? (
                <>
                  {t('disabledReason.belowThreshold')} <Money minor={minClaim} />
                </>
              )
            : null;

  const code = me.data?.member?.referralCode ?? '';
  const primaryDomain = useBrandingStore((st) => st.branding?.primaryDomain);
  const link = code ? buildReferralLink(shareOrigin(primaryDomain, window.location.origin), code) : '';

  const totalFriends =
    (friends.data?.direct.length ?? 0) + (friends.data?.sub.length ?? 0);

  function handleCopy() {
    if (!link) return;
    navigator.clipboard?.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }

  function handleShare() {
    if (!link) return;
    navigator.share?.({ url: link }).catch(() => {});
  }

  const actionBtnCls =
    'inline-flex h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-[var(--bg-elevated)] px-4 text-[13px] font-bold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-sunken)] hover:text-[var(--text-primary)] disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none';

  // Two readings of the same four sources: what is still claimable from each,
  // and what each has ever earned. The claimable row is what a player checks
  // before tapping; the lifetime row is what they check to see the programme
  // working. Shown one figure per source, players read it as whichever of the
  // two they happened to assume.
  //
  // The claimable figures are absent from a svc-commission that predates #97,
  // and `?? '0'` is the right answer there: a blank tile in a money column
  // reads as an outage, and zero is what an older service can honestly say
  // about a number it does not compute.
  const sourceGroups = [
    {
      key: 'group.claimable',
      tiles: [
        { key: 'source.loss', minor: BigInt(summary.data?.claimableLossShare ?? '0') },
        { key: 'source.turnover', minor: BigInt(summary.data?.claimableTurnoverShare ?? '0') },
        { key: 'source.ftd', minor: BigInt(summary.data?.claimableFtdBonus ?? '0') },
        { key: 'source.subLine', minor: BigInt(summary.data?.claimableSubLineShare ?? '0') },
      ],
    },
    {
      key: 'group.lifetime',
      tiles: [
        { key: 'source.loss', minor: BigInt(summary.data?.lossShare ?? '0') },
        { key: 'source.turnover', minor: BigInt(summary.data?.turnoverShare ?? '0') },
        { key: 'source.ftd', minor: BigInt(summary.data?.ftdBonus ?? '0') },
        { key: 'source.subLine', minor: BigInt(summary.data?.subLineShare ?? '0') },
      ],
    },
  ];

  const friendTiles = [
    { key: 'friendsLabel', count: totalFriends },
    { key: 'level.direct', count: friends.data?.direct.length ?? 0 },
    { key: 'level.sub', count: friends.data?.sub.length ?? 0 },
  ];

  return (
    <div className="space-y-4">
      <BonusSectionHeader title={t('title')} subtitle={t('subtitle')} />

      {/* Hero — claimable earnings */}
      <div className="rounded-2xl bg-[image:var(--art-2)] p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="text-[12px] font-semibold uppercase tracking-wide text-white/65">
              {t('claimableLabel')}
            </div>
            <div className="mt-1 text-3xl font-extrabold text-white">
              <Money minor={claimable} />
            </div>
            {summary.isError && (
              <div className="mt-1 text-[12px] text-white/60">{t('loadError')}</div>
            )}
          </div>
          <div className="flex w-full shrink-0 flex-col gap-1.5 sm:w-auto sm:items-end">
            <button
              type="button"
              className="h-11 w-full rounded-xl bg-white px-5 text-[14px] font-bold text-[#34146b] transition-colors hover:bg-white/90 disabled:cursor-not-allowed disabled:bg-white/15 disabled:text-white/50 sm:w-auto"
              disabled={
                summary.data?.enabled === false ||
                claimable <= 0n ||
                windowClosed ||
                outOfClaimsToday ||
                belowThreshold ||
                claim.isPending
              }
              onClick={() => claim.mutate()}
            >
              {claim.isPending ? t('claiming') : claimable > 0n ? t('claimCta') : t('nothingToClaim')}
            </button>
            {disabledReason && (
              <div className="text-[12px] text-white/70 sm:max-w-[16rem] sm:text-right">
                {disabledReason}
              </div>
            )}
          </div>
        </div>
        {cadenceNotes.length > 0 && (
          <div className="mt-4 space-y-1.5 rounded-xl bg-white/10 px-3 py-2.5">
            {cadenceNotes.map((note) => (
              <div key={note.key} className="flex items-start gap-2 text-[12px] text-white/75">
                <Icon name={note.icon} size={14} className="mt-[2px] shrink-0" />
                <span>{note.body}</span>
              </div>
            ))}
          </div>
        )}
        {!summary.isError && (
          <div className="mt-4 space-y-3">
            {sourceGroups.map((group) => (
              <div key={group.key}>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-white/55">
                  {t(group.key)}
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {group.tiles.map((tile) => (
                    <div key={tile.key} className="rounded-lg bg-white/10 px-3 py-2">
                      <div className="text-[11px] text-white/60">{t(tile.key)}</div>
                      <div className="truncate text-[13px] font-bold text-white">
                        <Money minor={tile.minor} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Referral link + share */}
      <Card className="p-4">
        <div className="text-[12px] font-semibold text-[var(--text-muted)]">{t('linkLabel')}</div>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <div className="flex h-11 min-w-0 flex-1 items-center rounded-lg bg-[var(--bg-elevated)] px-3">
            <span className="truncate font-mono text-[13px] text-[var(--text-secondary)]">
              {link || '—'}
            </span>
          </div>
          <div className="flex gap-2">
            <button type="button" className={actionBtnCls} disabled={!link} onClick={handleCopy}>
              <Icon name={copied ? 'check' : 'copy'} size={15} />
              {copied ? t('copied') : t('copy')}
            </button>
            <button type="button" className={actionBtnCls} disabled={!link} onClick={handleShare}>
              <Icon name="share" size={15} />
              {t('share')}
            </button>
          </div>
        </div>
      </Card>

      {/* Friends stats */}
      <div className="grid grid-cols-3 gap-3">
        {friendTiles.map((f) => (
          <Card key={f.key} className="p-4">
            <div className="flex items-center gap-1.5 text-[12px] text-[var(--text-muted)]">
              {f.key === 'friendsLabel' && <Icon name="users" size={14} />}
              <span className="truncate">{t(f.key)}</span>
            </div>
            <div className="mt-1 text-xl font-extrabold tabular-nums">
              {t('friendsCount', { count: f.count })}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
